(function() {
    'use strict';

    // Only run on cart page
    if (window.location.pathname !== '/cart') return;

    var lineGroupMap = {}; // line_number -> group_id
    var isCleaning = false; // Prevent concurrent cleanup runs

    // ===== CORE: Fetch cart and build group mapping =====
    function loadCartGroups() {
        return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
            .then(function(r) { return r.json(); })
            .then(function(cart) {
                lineGroupMap = {};
                cart.items.forEach(function(item, index) {
                    var lineNum = index + 1;
                    var props = item.properties || {};
                    var groupId = props['_group_id'] || null;
                    if (groupId) {
                        lineGroupMap[lineNum] = groupId;
                    }
                });
                return { cart: cart, groups: lineGroupMap };
            });
    }

    // ===== SELF-HEALING: Find and remove orphaned upgrade packs =====
    // If a main product (_is_main) was removed but its upgrade packs remain,
    // remove those orphaned upgrade packs automatically.
    function cleanOrphanedUpgrades() {
        if (isCleaning) return Promise.resolve();
        isCleaning = true;

        return loadCartGroups().then(function(result) {
            var cart = result.cart;
            var groups = {};
            // Group all items by _group_id
            cart.items.forEach(function(item, index) {
                var props = item.properties || {};
                var gid = props['_group_id'];
                if (!gid) return;
                if (!groups[gid]) groups[gid] = { items: [], hasMain: false };
                groups[gid].items.push({ line: index + 1, isMain: props['_is_main'] === 'yes' });
                if (props['_is_main'] === 'yes') groups[gid].hasMain = true;
            });

            // Find orphaned lines (group has no main product)
            var orphanedLines = [];
            Object.keys(groups).forEach(function(gid) {
                if (!groups[gid].hasMain) {
                    groups[gid].items.forEach(function(it) {
                        orphanedLines.push(it.line);
                    });
                }
            });

            if (orphanedLines.length === 0) {
                isCleaning = false;
                return Promise.resolve();
            }

            // Sort descending to avoid line number shifting
            orphanedLines.sort(function(a, b) { return b - a; });

            // Remove orphaned items one by one
            var chain = Promise.resolve();
            orphanedLines.forEach(function(lineNum) {
                chain = chain.then(function() {
                    return fetch('/cart/change.js', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ line: lineNum, quantity: 0 })
                    }).then(function(r) { return r.json(); });
                });
            });

            return chain.then(function() {
                isCleaning = false;
                // Reload page to show cleaned cart
                window.location.reload();
            });
        }).catch(function() {
            isCleaning = false;
        });
    }

    // ===== BUTTON INTERCEPTION: Remove entire group when any item is removed =====
    function findLineNumberFromElement(el) {
        // Method 1: href contains line=N
        var href = (el.getAttribute && el.getAttribute('href')) || '';
        var lineMatch = href.match(/line=(\d+)/);
        if (lineMatch) return parseInt(lineMatch[1]);

        // Method 2: data-line / data-cart-item / data-index on element or ancestor
        var cur = el;
        while (cur && cur !== document.body) {
            if (cur.hasAttribute && cur.hasAttribute('data-line')) {
                return parseInt(cur.getAttribute('data-line'));
            }
            if (cur.hasAttribute && cur.hasAttribute('data-cart-item')) {
                return parseInt(cur.getAttribute('data-cart-item'));
            }
            if (cur.hasAttribute && cur.hasAttribute('data-index')) {
                return parseInt(cur.getAttribute('data-index')) + 1;
            }
            cur = cur.parentElement;
        }

        // Method 3: closest row among known cart row selectors
        var rowSelectors = [
            '[data-cart-item]', '.cart-item', '.cart__row', 'tr[data-line]',
            'cart-items > div', '.cart-line-item', '.cart-item--root',
            '[data-cart-item-index]', '.cart__item', '.line-item',
            '[data-item-index]', '.cart-item-row'
        ];
        for (var s = 0; s < rowSelectors.length; s++) {
            var rows = document.querySelectorAll(rowSelectors[s]);
            if (rows.length > 0) {
                for (var i = 0; i < rows.length; i++) {
                    if (rows[i].contains(el) || rows[i] === el) return i + 1;
                }
            }
        }

        // Method 4: closest tr / li / div in cart form
        var closestRow = el.closest ? el.closest('tr, .cart-item, li, [data-cart-item], .cart__row, .cart__item, .line-item, .cart-item-row') : null;
        if (closestRow) {
            var siblings = closestRow.parentElement.children;
            for (var j = 0; j < siblings.length; j++) {
                if (siblings[j] === closestRow) return j + 1;
            }
        }

        return null;
    }

    // Remove ALL items belonging to a group
    function removeAllGroupItems(groupId) {
        var linesToRemove = [];
        for (var line in lineGroupMap) {
            if (lineGroupMap[line] === groupId) {
                linesToRemove.push(parseInt(line));
            }
        }

        if (linesToRemove.length === 0) return Promise.resolve();

        // Sort descending: remove higher line numbers first to avoid shifting
        linesToRemove.sort(function(a, b) { return b - a; });

        var chain = Promise.resolve();
        linesToRemove.forEach(function(lineNum) {
            chain = chain.then(function() {
                return fetch('/cart/change.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ line: lineNum, quantity: 0 })
                }).then(function(r) { return r.json(); });
            });
        });
        return chain;
    }

    // Intercept remove button clicks
    function interceptRemovals() {
        var removeSelectors = [
            'a[href*="/cart/change"]',
            'a[href*="cart/change"]',
            'a[href*="quantity=0"]',
            'button[data-cart-remove]',
            'button[data-action="remove"]',
            'button[onclick*="cart/change"]',
            'button[onclick*="quantity=0"]',
            '.cart-remove',
            '.cart__remove',
            '.cart-item__remove',
            '.remove-item',
            'a[title*="Remove"]',
            'a[title*="remove"]',
            'a[aria-label*="Remove"]',
            'a[aria-label*="remove"]',
            'button[aria-label*="Remove"]',
            'button[aria-label*="remove"]',
            'button[type="submit"][formaction*="cart/change"]',
            '.cart__remove a',
            '.cart-item a.remove',
            'a.cart__remove',
            'button.cart__remove',
            '[data-cart-item-remove]',
            '[data-action="delete-item"]'
        ];

        removeSelectors.forEach(function(selector) {
            var buttons;
            try { buttons = document.querySelectorAll(selector); } catch(e) { return; }
            Array.prototype.forEach.call(buttons, function(btn) {
                if (btn.getAttribute('data-diy-intercepted')) return;
                btn.setAttribute('data-diy-intercepted', 'true');

                btn.addEventListener('click', function(e) {
                    var lineNum = findLineNumberFromElement(btn);
                    if (!lineNum || !lineGroupMap[lineNum]) {
                        // Not a DIY item — let Shopify handle normally
                        // But still run cleanup after page reload
                        return;
                    }

                    e.preventDefault();
                    e.stopPropagation();
                    e.stopImmediatePropagation();

                    var groupId = lineGroupMap[lineNum];

                    // Remove ALL items in this group (main product + all upgrade packs)
                    removeAllGroupItems(groupId)
                        .then(function() {
                            window.location.href = '/cart';
                        })
                        .catch(function() {
                            window.location.href = '/cart';
                        });
                }, true); // capture phase
            });
        });
    }

    // Intercept quantity input set to zero via form submit
    function interceptQuantityInputs() {
        var qtyInputs = document.querySelectorAll(
            'input[name*="quantity"], input[name="updates[]"], .cart__qty-input, [data-quantity-input], input[data-quantity]'
        );
        Array.prototype.forEach.call(qtyInputs, function(input) {
            if (input.getAttribute('data-diy-intercepted')) return;
            input.setAttribute('data-diy-intercepted', 'true');

            var form = input.closest('form');
            if (!form) return;

            if (!form.getAttribute('data-diy-intercepted')) {
                form.setAttribute('data-diy-intercepted', 'true');
                form.addEventListener('submit', function(e) {
                    // Check if any DIY item quantity was set to 0
                    var allInputs = form.querySelectorAll('input[name*="quantity"], input[name="updates[]"]');
                    var groupsToRemove = [];

                    Array.prototype.forEach.call(allInputs, function(inp) {
                        if (parseInt(inp.value) === 0) {
                            var lineNum = findLineNumberFromElement(inp);
                            if (lineNum && lineGroupMap[lineNum]) {
                                groupsToRemove.push(lineGroupMap[lineNum]);
                            }
                        }
                    });

                    if (groupsToRemove.length > 0) {
                        e.preventDefault();
                        e.stopPropagation();

                        // Collect ALL lines belonging to affected groups
                        var uniqueGroups = {};
                        groupsToRemove.forEach(function(gid) { uniqueGroups[gid] = true; });

                        var linesToRemove = [];
                        for (var line in lineGroupMap) {
                            if (uniqueGroups[lineGroupMap[line]]) {
                                linesToRemove.push(parseInt(line));
                            }
                        }

                        // Also handle non-DIY items set to 0
                        var nonDiyChanges = [];
                        Array.prototype.forEach.call(allInputs, function(inp) {
                            if (parseInt(inp.value) === 0) {
                                var ln = findLineNumberFromElement(inp);
                                if (ln && !lineGroupMap[ln]) {
                                    nonDiyChanges.push(ln);
                                }
                            }
                        });

                        var allLines = linesToRemove.concat(nonDiyChanges);
                        // Deduplicate
                        var seen = {};
                        allLines = allLines.filter(function(l) {
                            if (seen[l]) return false;
                            seen[l] = true;
                            return true;
                        });
                        allLines.sort(function(a, b) { return b - a; });

                        var chain = Promise.resolve();
                        allLines.forEach(function(lineNum) {
                            chain = chain.then(function() {
                                return fetch('/cart/change.js', {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ line: lineNum, quantity: 0 })
                                }).then(function(r) { return r.json(); });
                            });
                        });

                        chain.then(function() {
                            window.location.href = '/cart';
                        }).catch(function() {
                            window.location.href = '/cart';
                        });
                    }
                }, true);
            }
        });
    }

    // Hide internal properties from cart display
    function hideInternalProperties() {
        var propSelectors = ['dt', 'dd', 'li', 'span', 'div', 'p'];
        propSelectors.forEach(function(sel) {
            var els = document.querySelectorAll(sel);
            Array.prototype.forEach.call(els, function(el) {
                if (el.children.length === 0) {
                    var text = (el.textContent || '').trim();
                    if (text.indexOf('_group_id') >= 0 ||
                        text.indexOf('_is_main') >= 0 ||
                        text.match(/^DIY-\d+-\d+$/)) {
                        el.style.display = 'none';
                        if (el.nextElementSibling) {
                            el.nextElementSibling.style.display = 'none';
                        }
                    }
                }
            });
        });
    }

    // ===== INITIALIZATION =====
    function init() {
        // Step 1: Run self-healing cleanup first (removes orphaned upgrade packs)
        cleanOrphanedUpgrades().then(function() {
            // Step 2: If no cleanup happened (no reload), continue with interception
            // Reload groups for current state
            return loadCartGroups();
        }).then(function() {
            hideInternalProperties();
            interceptRemovals();
            interceptQuantityInputs();
        }).catch(function() {
            // Fallback: at least try to intercept
            interceptRemovals();
            interceptQuantityInputs();
        });
    }

    // Wait for DOM ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

    // ===== AJAX CART MONITORING =====
    // Use MutationObserver to detect cart DOM changes (for themes with AJAX cart)
    var cartForm = document.querySelector('form[action*="/cart"]');
    if (cartForm) {
        var observer = new MutationObserver(function(mutations) {
            // Debounce: only run after mutations settle
            clearTimeout(window._diyCleanupTimer);
            window._diyCleanupTimer = setTimeout(function() {
                loadCartGroups().then(function() {
                    hideInternalProperties();
                    interceptRemovals();
                    interceptQuantityInputs();
                });
            }, 300);
        });
        observer.observe(cartForm, { childList: true, subtree: true });
    }

    // Also run cleanup on page show (handles back/forward navigation)
    window.addEventListener('pageshow', function(event) {
        if (event.persisted) {
            // Page restored from bfcache — re-run cleanup
            cleanOrphanedUpgrades();
        }
    });

})();